import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { container } from "tsyringe";
import UpcomingActionResponseResource from "common/resources/Project/Config/UpcomingAction/UpcomingActionResponseResource";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import LazyImage from "components/elements/LazyImage";
import Typography from "components/elements/Typography";
import { useProjectId } from "hooks/useProjectId";
import UpcomingActionService from "services/projects/UpcomingActionService";
import { translateISO369 } from "utils/I18nText";
import ManageCardMenu from "../../../components/ManageCardMenu";
import ActionProgressTag from "./ActionProgressTag";
import classes from "./classes.module.scss";

type IProps = {
	action: UpcomingActionResponseResource;
	onEditClick: () => void;
	onDeleteClick: () => void;
};

const upcomingActionService = container.resolve(UpcomingActionService);

export default function ActionCard(props: IProps) {
	const { imageUrl } = useActionInfo(props.action);

	return (
		<div className={classes["root"]}>
			<ManageCardMenu onEditClick={props.onEditClick} onDeleteClick={props.onDeleteClick} className={classes["card-menu"]} />

			<LazyImage src={imageUrl} alt="" className={classes["image"]} />

			<div className={classes["action-info-container"]}>
				<div className={classes["action-info"]}>
					<Typography type="h4" weight="bold">
						{translateISO369(props.action.title)}
					</Typography>
					{props.action.startsAt && props.action.endsAt && <ActionProgressTag startsAt={props.action.startsAt} endsAt={props.action.endsAt} />}
					<Typography type="p" weight="regular" className={classes["description"]}>
						{translateISO369(props.action.description)}
					</Typography>
				</div>
				{props.action.buttonLabel && props.action.buttonLink && (
					<Link to={props.action.buttonLink} target="_blank">
						<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY}>
							{translateISO369(props.action.buttonLabel)}
						</Button>
					</Link>
				)}
			</div>
		</div>
	);
}

export const useActionInfo = (action: UpcomingActionResponseResource) => {
	const { projectId } = useProjectId();
	const [imageUrl, setImageUrl] = useState<string | null>(null);
	const [imageFile, setImageFile] = useState<File | null>(null);

	const clearImage = useCallback(() => {
		setImageUrl(null);
		setImageFile(null);
	}, []);

	const fetchImage = useCallback(() => {
		const { image } = action;

		if (!image) return clearImage();

		return upcomingActionService.downloadImage(projectId, action.id).then((blob) => {
			setImageUrl(URL.createObjectURL(blob));
			setImageFile(
				new File([blob], image.name ?? "Action", {
					type: blob?.type || image.mimeType,
				}),
			);
		});
	}, [action, projectId, clearImage]);

	useEffect(() => {
		fetchImage();
	}, [fetchImage]);

	return { imageUrl, imageFile };
};
